<template>
  <v-card elevation="1" class="container-card">
    <v-row align="center" justify="start" class="top-bar pa-3">
      <v-col cols="12">
        <div class="d-flex justify-start align-center">
          <v-icon class="mr-2" icon="credit_card" />
          <div class="md-light">Credit Card</div>
          <v-spacer />
          <v-progress-circular
            v-if="loading"
            :indeterminate="true"
            size="25"
            width="2"
            class="loading-wheel"
          />
        </div>
      </v-col>
    </v-row>
    <v-col cols="12">
      <v-divider />
    </v-col>
    <v-row align="center" justify="start">
      <v-col cols="12">
        <v-card class="credit-card-form pa-3" elevation="0">
          <div class="d-flex flex-column justify-start">
            <div class="d-flex flex-column justify-end" style="min-width: 350px">
              <form id="payment-form">
                <div class="row">
                  <div class="field">
                    <div
                      id="stripe-form-card-number"
                      data-testid="add-order-card-number"
                      class="input empty"
                    ></div>
                    <label
                      for="stripe-form-card-number"
                      data-tid="elements.form.card_number_label"
                      >Card number</label
                    >
                    <div class="baseline"></div>
                  </div>
                </div>
                <div class="row">
                  <div class="field half-width">
                    <div
                      id="stripe-form-card-expiry"
                      data-testid="add-order-card-expiry"
                      class="input empty"
                    ></div>
                    <label
                      for="stripe-form-card-expiry"
                      data-tid="elements.form.card_expiry_label"
                      >Expiration</label
                    >
                    <div class="baseline"></div>
                  </div>
                  <div class="field half-width">
                    <div
                      id="stripe-form-card-cvc"
                      data-testid="add-order-card-cvc"
                      class="input empty"
                    ></div>
                    <label
                      for="stripe-form-card-cvc"
                      data-tid="elements.form.card_cvc_label"
                      >CVC</label
                    >
                    <div class="baseline"></div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" v-if="false">
        <div class="d-flex flex-column mt-4">
          <v-text-field
            id="stripe-form-card-number"
            label="Card Number"
            variant="outlined"
            class="credit-card-field"
            :rules="cardNumberRules"
            number
          />
          <div class="d-flex">
            <v-text-field
              label="MM"
              variant="outlined"
              id="stripe-form-card-expiry"
              class="credit-card-field mr-1"
              style="width: 75px"
              :rules="expirationMonthRules"
              number
            />
            <v-text-field
              label="YYYY"
              variant="outlined"
              id="stripe-form-card-expiry"
              class="credit-card-field mr-1 ml-1"
              style="width: 75px"
              :rules="expirationYearRules"
              number
            />
            <v-text-field
              label="CVC"
              id="stripe-form-card-cvc"
              variant="outlined"
              class="credit-card-field ml-1"
              style="width: 75px"
              :rules="[validateNotEmpty]"
              number
            />
          </div>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import store from "@/store";
import rules from "@/mixins/rules";
import helpers from "@/mixins/helpers";
import { loadStripe } from "@stripe/stripe-js";
import {
  buildTimePublishableKey,
  resolvePublishableKey,
} from "@/services/stripePublishableKey";
import StripeEvent from "@/models/StripeEvent";
import { mapGetters } from "vuex";
import "v-credit-card/dist/VCreditCard.css";

export default {
  components: {},
  mixins: [helpers, rules],

  props: {
    storeID: {
      type: Number,
      required: true,
    },
    campaign: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      // The CONNECTED-account instance: confirms a PaymentIntent (the
      // immediate-charge path), which lives on the distributor's account.
      stripe: null,
      // The PLATFORM instance: owns the card Elements and confirms a
      // SetupIntent (the batched path saves the card on the platform).
      setupStripe: null,
      // Build-time FALLBACK only -- overwritten from the campaign's
      // account-lookup payload before `loadStripe` runs.
      stripePublishableKey: buildTimePublishableKey(),

      salesTax: null,
      flatRate: null,
      companyAllowance: 0,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      stripe_account_id: null,
    };
  },

  computed: {
    ...mapGetters([
      "getShopperTotal",
      "getShopperTax",
      "getCheckoutFieldsValues",
    ]),
    cardNumberRules() {
      const rules = [];

      // form empty rule
      rules.push((v) => (!!v && v.length == 16) || "Card Number Invalid");

      return rules;
    },
    expirationMonthRules() {
      const rules = [];

      // form empty rule
      rules.push((v) => (!!v && v.length == 2) || "Month Invalid");
      rules.push((v) => (!!v && v > 0 && v <= 12) || "Month Invalid");
      return rules;
    },
    expirationYearRules() {
      const rules = [];

      // form empty rule
      rules.push((v) => (!!v && v.length == 4) || "Year Invalid");
      rules.push((v) => (!!v && v >= 2022) || "Year Invalid");
      return rules;
    },
  },

  watch: {},

  async mounted() {
    this.loadStripeInfo();
  },

  methods: {
    getCreditCardInfo() {
      return {
        cardNumber: this.cardNumber,
        cardExpiry: this.cardExpiry,
        cardCvc: this.cardCvc,
        stripe_account_id: this.stripe_account_id,
        stripe: this.stripe,
        setupStripe: this.setupStripe,
      };
    },

    clearCreditCardInfo() {
      this.cardNumber.clear();
      this.cardExpiry.clear();
      this.cardCvc.clear();
    },

    async loadStripeInfo() {
      if (!this.storeID) {
        return;
      }
      this.collectSalesTax = this.campaign.collect_sales_tax;
      // this.collectSalesTax = true;
      this.collectProcessingFee = this.campaign.collect_processing_fee;
      this.salesTax = this.campaign.sales_tax
        ? this.campaign.sales_tax
        : "flat-rate";
      if (this.salesTax != "tax-rate") {
        this.flatRate = this.campaign.flat_rate ? this.campaign.flat_rate : 0;
      }

      // Slice 2: dropped the legacy stores.company_allowance read here. The
      // charge is server-authoritative (Account.checkout); this field is not
      // consumed by the card-reader charge path.

      const result = await this.$axios.get(
        `/stripe/getStripeAccountIdFromCampaign/${this.campaign._jv.id}`
      );

      // The platform belongs to the STORE, not to the operator running the
      // card reader -- resolved from the same campaign-scoped payload that
      // carries the connected account.
      this.stripePublishableKey = resolvePublishableKey(result.data);

      if (this.campaign.payment_method == "Individual CC Payment") {
        if (result.data && result.data.stripe_account_id) {
          this.stripe_account_id = result.data.stripe_account_id;
          this.stripe = await loadStripe(this.stripePublishableKey, {
            stripeAccount: result.data.stripe_account_id,
          });
          this.setupStripe = await loadStripe(this.stripePublishableKey);
        }
      }

      // The Elements belong to the PLATFORM instance, as in the storefront
      // (CartCheckout initStripeElements). Whether the order saves the card
      // (platform confirmCardSetup) or charges it (connected
      // confirmCardPayment, reached with a single-use token) is the SERVER's
      // call, made only after the order is pre-created -- and an Element can
      // only be confirmed by the instance that created it.
      if (this.setupStripe) {
        var elements = this.setupStripe.elements();
        // Floating labels
        var inputs = document.querySelectorAll(
          ".cell.stripe-div.stripe-form .input"
        );
        Array.prototype.forEach.call(inputs, function (input) {
          input.addEventListener("focus", function () {
            input.classList.add("focused");
          });
          input.addEventListener("blur", function () {
            input.classList.remove("focused");
          });
          input.addEventListener("keyup", function () {
            if (input.value.length === 0) {
              input.classList.add("empty");
            } else {
              input.classList.remove("empty");
            }
          });
        });

        var elementStyles = {
          base: {
            color: "#32325D",
            fontWeight: 500,
            fontFamily: "Source Code Pro, Consolas, Menlo, monospace",
            fontSize: "16px",
            fontSmoothing: "antialiased",

            "::placeholder": {
              color: "#CFD7DF",
            },
            ":-webkit-autofill": {
              color: "#e39f48",
            },
          },
          invalid: {
            color: "#E25950",

            "::placeholder": {
              color: "#FFCCA5",
            },
          },
        };

        var elementClasses = {
          focus: "focused",
          empty: "empty",
          invalid: "invalid",
        };

        this.cardNumber = elements.create("cardNumber", {
          style: elementStyles,
          classes: elementClasses,
        });
        this.cardNumber.mount("#stripe-form-card-number");

        this.cardExpiry = elements.create("cardExpiry", {
          style: elementStyles,
          classes: elementClasses,
        });
        this.cardExpiry.mount("#stripe-form-card-expiry");

        this.cardCvc = elements.create("cardCvc", {
          style: elementStyles,
          classes: elementClasses,
        });
        this.cardCvc.mount("#stripe-form-card-cvc");
      }
    },
  },
};
</script>

<style scoped>
.container-card {
  height: 100%;
  width: 100%;
  border-radius: 5px;
}
.credit-card-field {
  border-radius: 25px;
}
.top-bar {
  background: white;
}
</style>

